import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";

import { DialogBox, Drawer, DrawerControl, MetaDrivenTable, PaginationControls, SearchField, StatusChip } from "../../../shared/components";
import { useGetResourceReq } from "../hooks/useGetResourceReq";
import { AddRequest, DeleteConfirmation, RequestDetails, RequestDetailsHeader } from "../components";
import { useDeleteRequest } from "../hooks/useDeleteRequest";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const ResourceRequests = () => {

    const { projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const { isAllowedToAddResourceRequest, isAllowedToEditResourceRequest, isAllowedToDeleteResourceRequest } = useCapabilities();

    const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
    const [deleteState, setDeleteState] = useState<{ isOpen: boolean; requestId: string | null }>({ isOpen: false, requestId: null });

    const openDetailsDrawer = searchParams.get("details") === "true";
    const openEditDrawer = searchParams.get("edit") === "true";
    const requestId = searchParams.get("requestId") || null;

    const handleOpenDetailsDrawer = (id: string) => {
        searchParams.set("details", "true");
        searchParams.set("requestId", id);
        setSearchParams(searchParams, { replace: true });
    }

    const handleOpenEditDrawer = (id: string) => {
        searchParams.set("edit", "true");
        searchParams.set("requestId", id);
        setSearchParams(searchParams, { replace: true });
    }

    const handleCloseDrawers = () => {
        searchParams.delete("details");
        searchParams.delete("edit");
        searchParams.delete("requestId");
        setSearchParams(searchParams, { replace: true });
    }

    const {
        data, isFetching, isPending, isError, error, searchDraft, pagination,
        refetch, changePage, changeRowsPerPage, handleChangeSearch, handleClear, handleSearch
    } = useGetResourceReq({ projectId: projectId });

    const { deleting, deleteError, handleDelete } = useDeleteRequest({
        onSuccess: () => {
            setDeleteState({ isOpen: false, requestId: null });
            refetch();
        }
    });

    const headers = [
        { "alias_name": "resource_ref", "label": "Resource REF", "display_order": 1, is_show: true },
        { "alias_name": "resource_type_display_text", "label": "Type", "display_order": 2, is_show: true },
        { "alias_name": "description", "label": "Description", "display_order": 3, is_show: true },
        { "alias_name": "status_display_text", "label": "Status", "display_order": 7, is_show: true },
        { "alias_name": "resource_supplier_display_text", "label": "Supplier", "display_order": 8, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 9, is_show: true },
        { "alias_name": "comments", "label": "Comments", "display_order": 10, is_show: true },
        { "alias_name": "project_title", "label": "Project Title", "display_order": 11, is_show: true },
        { "alias_name": "phase_name", "label": "Phase Name", "display_order": 12, is_show: true },
        { "alias_name": "activity_name", "label": "Activity Name", "display_order": 13, is_show: true },
        { "alias_name": "owner_name", "label": "Owner Name", "display_order": 14, is_show: true },
    ];

    const metrics = {
        status_display_text: {
            type: 'component',
            element: ((row: { status_display_text: string; }) => (
                <StatusChip label={row.status_display_text} color={
                    row.status_display_text === 'Requested' ? 'default' :
                        row.status_display_text === 'Approved' ? 'blue' :
                            row.status_display_text === 'Ordered' ? 'amber' :
                                row.status_display_text === 'Delivered' ? 'green' :
                                    row.status_display_text === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        order_placed_date: {
            type: 'date',
        },
        est_delivery_date: {
            type: 'date',
        },
        required_date: {
            type: 'date',
        }
    };

    if (isPending || isFetching) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {error?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>

            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={2} px={2}>
                <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                {isAllowedToAddResourceRequest && <Box display={'flex'} alignItems={'center'} py={2} gap={2}>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setIsNewRequestOpen(true)}>NEW RESOURCE TRACKING</Button>
                </Box>}
            </Box>

            <Box flex={1}>
                <MetaDrivenTable headers={headers} data={data} dataMetrics={metrics} sortDisabled
                    actions={[
                        {
                            label: <Visibility sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                handleOpenDetailsDrawer(row.id);
                            }
                        },
                        {
                            label: isAllowedToEditResourceRequest && <Edit sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                handleOpenEditDrawer(row.id);
                            }
                        },
                        {
                            label: isAllowedToDeleteResourceRequest && <Delete sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                setDeleteState({ isOpen: true, requestId: row.id });
                            }
                        }
                    ]}
                />
            </Box>

            <Box flexShrink={0}>
                <PaginationControls
                    total={pagination?.total}
                    rowsPerPage={pagination?.limit}
                    currentPage={pagination?.page}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <DrawerControl
                title={openEditDrawer ? "Edit Resource Tracking" : "New Resource Tracking"}
                subTitle={openEditDrawer ? "Update resource tracking details" : "Create a new resource tracking"}
                isOpen={isNewRequestOpen || openEditDrawer}
                handleClose={() => {
                    setIsNewRequestOpen(false);
                    handleCloseDrawers();
                }}
            >
                <AddRequest
                    projectId={projectId}
                    refetch={refetch}
                    requestId={requestId}
                    isEdit={openEditDrawer}
                    onSuccess={() => {
                        setIsNewRequestOpen(false);
                        handleCloseDrawers();
                    }}
                    onClose={() => {
                        setIsNewRequestOpen(false);
                        handleCloseDrawers();
                    }}
                />
            </DrawerControl>

            <Drawer
                isOpen={openDetailsDrawer}
                header={
                    <RequestDetailsHeader
                        onClose={() => handleCloseDrawers()}
                        projectId={projectId}
                        requestId={requestId}
                    />
                }
            >
                <RequestDetails
                    projectId={projectId}
                    requestId={requestId}
                />
            </Drawer>

            <DialogBox
                showModal={deleteState.isOpen}
                title="Delete Resource Tracking"
                subTitle=""
                onModalClose={() => setDeleteState({ isOpen: false, requestId: null })}
                fullWidth
                maxWidth="sm"
            >
                <DeleteConfirmation
                    deleting={deleting}
                    onConfirm={() => {
                        if (deleteState.requestId) {
                            handleDelete({ requestId: deleteState.requestId });
                        }
                    }}
                    onCancel={() => setDeleteState({ isOpen: false, requestId: null })}
                />
            </DialogBox>

        </Box>
    )
}